import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Recommendation } from '../../types';

interface RecommendationsSectionProps {
  recommendations: Recommendation[];
}

const RecommendationsSection: React.FC<RecommendationsSectionProps> = ({ recommendations }) => {
  const { t } = useTranslation();

  const getPriorityColor = (priority: string): string => {
    switch(priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-orange-100 text-orange-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getTypeEmoji = (type: string): string => {
    switch(type) {
      case 'skills': return '💻';
      case 'experience': return '📈';
      case 'education': return '🎓';
      case 'certifications': return '📜';
      case 'languages': return '🌍';
      case 'general': return '✨';
      default: return '📋';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5 flex items-center">
        <span className="text-2xl mr-3">💡</span>
        <h3 className="text-xl font-semibold">{t('results.recommendations.title')}</h3>
      </div>
      
      <div className="p-6">
        <div className="space-y-4">
          {recommendations?.length === 0 ? (
            <div className="text-gray-500 italic">{t('results.recommendations.none')}</div>
          ) : (
            recommendations?.map((rec, index) => (
              <div 
                key={index}
                className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200 
                          transition-all hover:shadow-md hover:-translate-y-1"
              >
                <div className="flex items-center mb-3">
                  <span className="text-xl mr-2">{getTypeEmoji(rec.type)}</span>
                  <h4 className="font-semibold text-gray-800">
                    {t(`results.recommendations.type.${rec.type}`, rec.type !== 'general' ? { defaultValue: rec.type } : undefined)}{rec.type !== 'general' ? ': ' : ''}{rec.title}
                  </h4>
                  <span className={`ml-auto text-xs font-bold uppercase px-2 py-1 rounded-full ${getPriorityColor(rec.priority)}`}>
                    {t(`results.recommendations.priority.${rec.priority}`)}
                  </span>
                </div>
                
                <p className="text-gray-700 mb-3">{rec.description}</p>
                
                {rec?.actionable?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm font-medium text-gray-600 mb-1">{t('results.recommendations.actions')}</div>
                    <div className="flex flex-wrap gap-2">
                      {rec?.actionable?.map((action, idx) => (
                        <span key={idx} className="text-xs px-3 py-1 bg-white text-indigo-700 rounded-full border border-indigo-200">
                          {action}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {rec?.actionable?.length === 0 && (
                  <div className="text-gray-500 italic">{t('results.recommendations.noActions')}</div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendationsSection;